/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-01 11:07:18
 **/

import { useCallback } from "react";
import { useAtomCallback } from "jotai/utils";
import { JotaiStore } from "@/frameworks/libraries/commons";

import { PhoneCallAtoms } from "@/frameworks/core/hooks";

const DefaultMediaInfo: PushServices.Call.UserMediaInfo = {
	audio: "on",
	video: "off",
	screenShare: false,
	isRecording: false
};

export default function useUserAPI() {
	const get_users = useAtomCallback(
		useCallback((get, _, callID: string) => {
			return get(PhoneCallAtoms.CallUsers(callID));
		}, []),
		{ store: JotaiStore }
	);

	const find_user = useAtomCallback(
		useCallback((get, set, callID: string, userID: string) => {
			return get(PhoneCallAtoms.CallUsers(callID))?.[userID] ?? null;
		}, []),
		{ store: JotaiStore }
	);

	const add_or_update_user = useAtomCallback(
		useCallback(
			(_, set, callID: string, userID: string, userInfo: JotaiArgument<Partial<PushServices.Call.UserInfo>>) => {
				set(PhoneCallAtoms.CallUsers(callID), (prev) => ({
					...prev,
					[userID]: {
						...prev[userID],
						...(typeof userInfo === "function" ? userInfo(prev[userID]) : userInfo)
					}
				}));
			},
			[]
		),
		{ store: JotaiStore }
	);

	const get_user_media = useAtomCallback(
		useCallback((get, set, callID: string, userID: string) => {
			return ["ACCEPTED", "REQUESTED"].includes(get(PhoneCallAtoms.CallUsers(callID))?.[userID]?.status)
				? get(PhoneCallAtoms.CallUsers(callID))?.[userID]?.media ?? DefaultMediaInfo
				: DefaultMediaInfo;
		}, []),
		{ store: JotaiStore }
	);

	const set_user_media = useAtomCallback(
		useCallback((get, set, callID: string, userID: string, media: Partial<PushServices.Call.UserMediaInfo>) => {
			const prevUsers = get(PhoneCallAtoms.CallUsers(callID));
			prevUsers &&
				set(PhoneCallAtoms.CallUsers(callID), (prev) => ({
					...prev,
					[userID]: {
						...prev[userID],
						media: {
							...(prev[userID]?.media ?? DefaultMediaInfo),
							...media
						}
					}
				}));
		}, []),
		{ store: JotaiStore }
	);

	return {
		get_users,
		find_user,
		add_or_update_user,
		get_user_media,
		set_user_media,
		default_user_media_info: DefaultMediaInfo
	};
}
